import { c as _c } from "react/compiler-runtime";
import { SmartDatePickerPanel } from "../../smart-date-picker-panel";
import Switch from "../../switch";
import { formatMonthDay } from "../table-filters/helpers";
import { format } from "date-fns";
import React, { useState } from "react";
import { type DateRange } from "react-day-picker";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useFilterEdit } from "./edit-context";
import { BooleanFilter, DateSelect, DollarFilter, type FilterInputProps, MultiSelectInput, NumberFilter, NumberRangeFilter, PhoneNumberFilter, ProviderFilter, SingleSelectInput, SortFilter, TextFilter } from "./filter-types";
import { type SortOption } from "./filter-types/sort";
import { type FilterTypes } from "./filter-types/types";

/**
 * Width cap for the popover that hosts a filter input. Option lists are
 * capped at the Filters panel's width so long labels (e.g. promotion names)
 * wrap instead of pushing the popover off-screen. Every other input has a
 * fixed intrinsic width (the date range filter shows a two-month calendar), so it is
 * only bounded by the viewport space Radix reports.
 */
const OPTION_LIST_TYPES = new Set<FilterTypes>(["select", "multiselect", "location", "provider", "sort"]);
export const filterContentWidthClass = (type: FilterTypes) => OPTION_LIST_TYPES.has(type) ? "max-w-[min(280px,var(--radix-popper-available-width))]" : "max-w-[var(--radix-popper-available-width)]";

/**
 * Picks the appropriate filter input for the active filter type. Reads the
 * wrapper's `FilterEditContext` so the per-type rendering doesn't drift
 * between DropdownTableFilter and InlinePillTableFilter.
 */
export const FilterInput: React.FC = () => {
  const $ = _c(73);
  const {
    state: t0,
    actions: t1,
    meta: t2
  } = useFilterEdit();
  const {
    value
  } = t0;
  const {
    onChange,
    submit: onSubmit
  } = t1;
  const {
    type,
    label,
    options,
    onBack,
    focusRef,
    direction,
    openEnded
  } = t2;
  let t3;
  if ($[0] !== focusRef || $[1] !== label || $[2] !== onBack || $[3] !== onChange || $[4] !== onSubmit || $[5] !== options || $[6] !== value) {
    t3 = {
      value,
      onChange,
      label,
      options,
      onBack,
      focusRef,
      onSubmit
    };
    $[0] = focusRef;
    $[1] = label;
    $[2] = onBack;
    $[3] = onChange;
    $[4] = onSubmit;
    $[5] = options;
    $[6] = value;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const commonProps = t3;
  switch (type) {
    case "toggle":
      {
        const t4 = value === true;
        let t5;
        if ($[8] !== onChange) {
          t5 = checked => onChange(checked ? true : undefined);
          $[8] = onChange;
          $[9] = t5;
        } else {
          t5 = $[9];
        }
        let t6;
        if ($[10] !== focusRef || $[11] !== label || $[12] !== t4 || $[13] !== t5) {
          t6 = <Switch ref={focusRef} aria-label={label} checked={t4} onCheckedChange={t5} />;
          $[10] = focusRef;
          $[11] = label;
          $[12] = t4;
          $[13] = t5;
          $[14] = t6;
        } else {
          t6 = $[14];
        }
        return t6;
      }
    case "boolean":
      {
        let t4;
        if ($[15] !== focusRef || $[16] !== label || $[17] !== onChange || $[18] !== options || $[19] !== value) {
          t4 = <BooleanFilter value={value} onChange={onChange} options={options} label={label} focusRef={focusRef} />;
          $[15] = focusRef;
          $[16] = label;
          $[17] = onChange;
          $[18] = options;
          $[19] = value;
          $[20] = t4;
        } else {
          t4 = $[20];
        }
        return t4;
      }
    case "id":
      {
        if (Array.isArray(value)) {
          let t4;
          if ($[21] !== value) {
            t4 = value.join(", ");
            $[21] = value;
            $[22] = t4;
          } else {
            t4 = $[22];
          }
          let t5;
          if ($[23] !== onChange) {
            t5 = text => onChange((text ?? "").split(/[\s,]+/).filter(_temp));
            $[23] = onChange;
            $[24] = t5;
          } else {
            t5 = $[24];
          }
          let t6;
          if ($[25] !== commonProps || $[26] !== t4 || $[27] !== t5) {
            t6 = <TextFilter {...commonProps} value={t4} onChange={t5} />;
            $[25] = commonProps;
            $[26] = t4;
            $[27] = t5;
            $[28] = t6;
          } else {
            t6 = $[28];
          }
          return t6;
        }
        let t4;
        if ($[29] !== commonProps) {
          t4 = <TextFilter {...commonProps} />;
          $[29] = commonProps;
          $[30] = t4;
        } else {
          t4 = $[30];
        }
        return t4;
      }
    case "text":
      {
        let t4;
        if ($[31] !== commonProps) {
          t4 = <TextFilter {...commonProps} />;
          $[31] = commonProps;
          $[32] = t4;
        } else {
          t4 = $[32];
        }
        return t4;
      }
    case "phoneNumber":
      {
        let t4;
        if ($[33] !== commonProps) {
          t4 = <PhoneNumberFilter {...commonProps} />;
          $[33] = commonProps;
          $[34] = t4;
        } else {
          t4 = $[34];
        }
        return t4;
      }
    case "number":
      {
        let t4;
        if ($[35] !== commonProps) {
          t4 = <NumberFilter {...commonProps} />;
          $[35] = commonProps;
          $[36] = t4;
        } else {
          t4 = $[36];
        }
        return t4;
      }
    case "select":
      {
        let t4;
        if ($[37] !== onChange || $[38] !== options || $[39] !== value) {
          t4 = <SingleSelectInput value={value} options={options} onChange={onChange} />;
          $[37] = onChange;
          $[38] = options;
          $[39] = value;
          $[40] = t4;
        } else {
          t4 = $[40];
        }
        return t4;
      }
    case "dollar":
      {
        let t4;
        if ($[41] !== onChange || $[42] !== onSubmit || $[43] !== value) {
          t4 = <DollarFilter value={value} onChange={onChange} onSubmit={onSubmit} />;
          $[41] = onChange;
          $[42] = onSubmit;
          $[43] = value;
          $[44] = t4;
        } else {
          t4 = $[44];
        }
        return t4;
      }
    case "number-range":
      {
        let t4;
        if ($[45] !== onChange || $[46] !== onSubmit || $[47] !== value) {
          t4 = <NumberRangeFilter value={value} onChange={onChange} onSubmit={onSubmit} />;
          $[45] = onChange;
          $[46] = onSubmit;
          $[47] = value;
          $[48] = t4;
        } else {
          t4 = $[48];
        }
        return t4;
      }
    case "multiselect":
    case "location":
      {
        let t4;
        if ($[49] !== value) {
          t4 = Array.isArray(value) ? value : [];
          $[49] = value;
          $[50] = t4;
        } else {
          t4 = $[50];
        }
        let t5;
        if ($[51] !== onChange || $[52] !== options || $[53] !== t4) {
          t5 = <MultiSelectInput value={t4} options={options} onChange={onChange} />;
          $[51] = onChange;
          $[52] = options;
          $[53] = t4;
          $[54] = t5;
        } else {
          t5 = $[54];
        }
        return t5;
      }
    case "date":
      {
        let t4;
        if ($[55] !== direction || $[56] !== onChange || $[57] !== openEnded || $[58] !== value) {
          t4 = <DateFilterInput value={value} onChange={onChange} direction={direction} mode="date" openEnded={openEnded} />;
          $[55] = direction;
          $[56] = onChange;
          $[57] = openEnded;
          $[58] = value;
          $[59] = t4;
        } else {
          t4 = $[59];
        }
        return t4;
      }
    case "birthday":
      {
        let t4;
        if ($[60] !== direction || $[61] !== onChange || $[62] !== value) {
          t4 = <DateFilterInput value={value} onChange={onChange} direction={direction} mode="birthday" />;
          $[60] = direction;
          $[61] = onChange;
          $[62] = value;
          $[63] = t4;
        } else {
          t4 = $[63];
        }
        return t4;
      }
    case "date-select":
      {
        let t4;
        if ($[64] !== onChange) {
          t4 = <DateSelect onChange={onChange} />;
          $[64] = onChange;
          $[65] = t4;
        } else {
          t4 = $[65];
        }
        return t4;
      }
    case "sort":
      {
        const t4 = options as SortOption[];
        let t5;
        if ($[66] !== onChange || $[67] !== t4 || $[68] !== value) {
          t5 = <SortFilter onChange={onChange} options={t4} value={value} />;
          $[66] = onChange;
          $[67] = t4;
          $[68] = value;
          $[69] = t5;
        } else {
          t5 = $[69];
        }
        return t5;
      }
    case "provider":
      {
        let t4;
        if ($[70] !== onChange || $[71] !== value) {
          t4 = <ProviderFilter value={value} onChange={onChange} />;
          $[70] = onChange;
          $[71] = value;
          $[72] = t4;
        } else {
          t4 = $[72];
        }
        return t4;
      }
    default:
      {
        return null;
      }
  }
};
type DateFilterMode = "date" | "birthday";

/**
 * Date filter input — handles the [startDate, endDate] string ↔ DateRange
 * conversion and renders SmartDatePickerPanel directly inside the table
 * filter dropdown (no text-typing layer).
 *
 * In "birthday" mode values are emitted as `--MM-DD` (year-agnostic) instead
 * of `yyyy-MM-dd`, matching the backend's month-day birthday SQL path.
 */
const DateFilterInput: React.FC<{
  value: any;
  onChange: (value: any) => void;
  direction?: "past" | "future";
  mode?: DateFilterMode;
  openEnded?: boolean;
}> = t0 => {
  const $ = _c(32);
  const {
    value,
    onChange,
    direction: t1,
    mode: t2,
    openEnded: t3
  } = t0;
  const direction = t1 === undefined ? "past" : t1;
  const mode = t2 === undefined ? "date" : t2;
  const openEnded = t3 === undefined ? true : t3;
  const isBirthday = mode === "birthday";
  const clock = useClinicClock();
  const [pendingRange, setPendingRange] = useState(undefined);
  let t4;
  if ($[0] !== isBirthday) {
    t4 = val => {
      if (!val) {
        return;
      }
      if (isBirthday && val.startsWith("--")) {
        const [m, d] = val.slice(2).split("-");
        return new Date(new Date().getFullYear(), parseInt(m) - 1, parseInt(d));
      }
      return new Date(val + "T00:00:00");
    };
    $[0] = isBirthday;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const parseDateValue = t4;
  let t5;
  if ($[2] !== isBirthday) {
    t5 = date => isBirthday ? formatMonthDay(date) : format(date, "yyyy-MM-dd");
    $[2] = isBirthday;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const formatValue = t5;
  let t6;
  bb0: {
    if (Array.isArray(value) && value.length === 2) {
      let t7;
      if ($[4] !== parseDateValue || $[5] !== value[0]) {
        t7 = parseDateValue(value[0]);
        $[4] = parseDateValue;
        $[5] = value[0];
        $[6] = t7;
      } else {
        t7 = $[6];
      }
      const fromDate = t7;
      let toDate;
      if ($[7] !== fromDate || $[8] !== isBirthday || $[9] !== parseDateValue || $[10] !== value[1]) {
        toDate = parseDateValue(value[1]);
        if (isBirthday && fromDate && toDate && fromDate > toDate) {
          toDate.setFullYear(toDate.getFullYear() + 1);
        }
        $[7] = fromDate;
        $[8] = isBirthday;
        $[9] = parseDateValue;
        $[10] = value[1];
        $[11] = toDate;
      } else {
        toDate = $[11];
      }
      if (fromDate || toDate) {
        let t8;
        if ($[12] !== fromDate || $[13] !== toDate) {
          t8 = {
            from: fromDate,
            to: toDate
          };
          $[12] = fromDate;
          $[13] = toDate;
          $[14] = t8;
        } else {
          t8 = $[14];
        }
        t6 = t8;
        break bb0;
      }
    }
    t6 = undefined;
  }
  const currentDateRange = t6;
  const displayRange = pendingRange ?? currentDateRange;
  let t7;
  if ($[15] !== clock || $[16] !== onChange) {
    t7 = kind => {
      const today = clock.today();
      onChange(kind === "after" ? [today, "9000-12-31"] : ["1970-01-01", today]);
      setPendingRange(undefined);
    };
    $[15] = clock;
    $[16] = onChange;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const applyOpenEnded = t7;
  let t8;
  if ($[18] !== applyOpenEnded || $[19] !== isBirthday || $[20] !== openEnded) {
    t8 = !isBirthday && openEnded && <div className="flex gap-2"><button type="button" onClick={() => applyOpenEnded("after")} className="rounded-md border border-border px-2.5 py-1 text-caption text-foreground-secondary hover:bg-hover">After today</button><button type="button" onClick={() => applyOpenEnded("before")} className="rounded-md border border-border px-2.5 py-1 text-caption text-foreground-secondary hover:bg-hover">Before today</button></div>;
    $[18] = applyOpenEnded;
    $[19] = isBirthday;
    $[20] = openEnded;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== formatValue || $[23] !== onChange) {
    t9 = range => {
      const nextValue = [range?.from ? formatValue(range.from) : undefined, range?.to ? formatValue(range.to) : undefined];
      if (range?.from || range?.to) {
        onChange(nextValue);
        setPendingRange(range.from && range.to ? undefined : range);
      } else {
        onChange(undefined);
        setPendingRange(undefined);
      }
    };
    $[22] = formatValue;
    $[23] = onChange;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== direction || $[26] !== displayRange || $[27] !== t9) {
    t10 = <SmartDatePickerPanel mode="range" direction={direction} value={displayRange} onChange={t9} />;
    $[25] = direction;
    $[26] = displayRange;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t8) {
    t11 = <div className="space-y-2">{t8}{t10}</div>;
    $[29] = t10;
    $[30] = t8;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
};
function _temp(id) {
  return id.length > 0;
}